import {
  MESSAGE_MAX_ATTACHMENTS,
  MESSAGE_MAX_LENGTH,
  mentionedUserIds,
  type Attachment,
  type Channel,
  type Message,
  type Paginated,
} from '@nexo/types';
import { Avatar, IconButton, Kbd, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  AtSign,
  BarChart3,
  CalendarClock,
  Bold,
  Code,
  Italic,
  ListPlus,
  Paperclip,
  SendHorizontal,
  SquareCode,
  Strikethrough,
  Type,
  Users,
  X,
} from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type SubmitEvent,
} from 'react';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { formatBytes, formatDay, formatTime } from '../../lib/format';
import { WhenDialog } from '../../components/WhenDialog';
import { keys, useAttachmentsConfig } from '../../lib/queries';
import { useRealtime } from '../../realtime/RealtimeProvider';
import { useMe } from '../auth/AuthProvider';
import { channelLabel } from '../channels/ChannelSidebar';
import { FileIcon } from '../files/FileIcon';
import { roleColors } from '../teams/roles';
import { useCreateTask } from '../tasks/useTaskMutations';
import { CreatePollDialog } from './CreatePollDialog';
import { EmojiPicker } from './EmojiPicker';
import { editingStore } from './editing';
import { fromEditable, tokenAtCaret, wrapSelection } from './mentions';
import { QuoteBlock } from './QuoteBlock';
import { ScheduledMessages } from './ScheduledMessages';
import { replyStore, useReplyingTo } from './replying';
import { canIn } from '../../lib/permissions';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../lib/forms';

const TYPING_PING_MS = 2500;
const COUNTER_FROM = MESSAGE_MAX_LENGTH - 500;
const draftKey = (channelId: string) => `nexo.draft.${channelId}`;

interface PendingFile {
  localId: string;
  file: File;
  previewUrl: string | null;
  progress: number;
  attachment: Attachment | null;
  error: string | null;
}

interface Suggestion {
  key: string;
  label: string;
  hint?: string;
  icon: ReactNode;
  insert: string;
}

const commands = [
  {
    name: 'tarea',
    usage: '/tarea título @responsable',
    description: 'Crea una tarea en esta conversación',
  },
];

export interface ComposerHandle {
  addFiles: (files: File[]) => void;
}

export const Composer = forwardRef<
  ComposerHandle,
  {
    channel: Channel;
    /** Set in a thread panel: messages are sent as replies to this message. */
    parentId?: string;
  }
>(function Composer({ channel, parentId }, ref) {
  const draftId = parentId ? `${channel.id}.${parentId}` : channel.id;
  // Inline replies belong to the channel composer, not the thread one.
  const replyingToChannel = useReplyingTo(channel.id);
  const replyingTo = parentId ? null : replyingToChannel;
  const me = useMe();
  const { members, roles, online } = useTeam();
  const { client } = useRealtime();
  const queryClient = useQueryClient();
  const toast = useToast();
  const uploads = useAttachmentsConfig();
  const { t } = useTranslation();
  const createTask = useCreateTask();
  const [body, setBody] = useState(() => localStorage.getItem(draftKey(draftId)) ?? '');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [creatingPoll, setCreatingPoll] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const schedule = useMutation({
    mutationFn: (sendAt: string) =>
      api.scheduled.create(channel.id, {
        body: fromEditable(body.trim(), members, roles),
        sendAt,
        ...(parentId ? { parentId } : {}),
      }),
    onSuccess: (scheduled) => {
      setScheduling(false);
      reset();
      void queryClient.invalidateQueries({ queryKey: keys.scheduled(channel.id) });
      toast({
        title: t('Mensaje programado: {{day}}, {{time}}', {
          day: formatDay(scheduled.sendAt).toLowerCase(),
          time: formatTime(scheduled.sendAt),
        }),
        tone: 'success',
      });
    },
    onError: (err) =>
      toast({ title: t('No se pudo programar'), description: errorMessage(err), tone: 'error' }),
  });
  const [showFormatting, setShowFormatting] = useState(false);
  const [caret, setCaret] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);

  const canUpload = (uploads.data?.enabled ?? false) && canIn(channel, 'attach_files');
  const maxBytes = uploads.data?.maxBytes ?? 0;

  /** Caret/selection to apply once React has rendered a programmatic change. */
  const pendingSelection = useRef<[number, number] | null>(null);

  // Auto-grow up to a max height, then restore any programmatic caret position.
  useLayoutEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
    const selection = pendingSelection.current;
    if (selection) {
      pendingSelection.current = null;
      el.focus();
      el.setSelectionRange(selection[0], selection[1]);
      setCaret(selection[1]);
    }
  }, [body]);

  /** Replaces the text and places the caret/selection after the next render. */
  function setBodyWithSelection(next: string, start: number, end = start) {
    pendingSelection.current = [start, end];
    setBody(next);
  }

  // Keep an unsent draft per conversation.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (body.trim()) localStorage.setItem(draftKey(draftId), body);
      else localStorage.removeItem(draftKey(draftId));
    }, 300);
    return () => clearTimeout(timer);
  }, [body, draftId]);

  /* ---------- Suggestions: @mentions and /commands ---------- */

  const token = dismissedAt === caret ? null : tokenAtCaret(body, caret);
  const suggestions: Suggestion[] = (() => {
    if (!token) return [];
    const q = token.query.toLowerCase();
    if (token.kind === '/') {
      return commands
        .filter((c) => c.name.startsWith(q))
        .map((c) => ({
          key: c.name,
          label: t(c.usage),
          hint: t(c.description),
          icon: <ListPlus className="size-4" />,
          insert: `/${c.name} `,
        }));
    }
    const people: Suggestion[] = members
      .filter((m) => m.id !== me.id && m.displayName.toLowerCase().includes(q))
      .slice(0, 8)
      .map((m) => ({
        key: m.id,
        label: m.displayName,
        hint: m.title ?? undefined,
        icon: (
          <Avatar
            id={m.id}
            name={m.displayName}
            src={m.avatarUrl}
            size="xs"
            online={online ? online.has(m.id) : undefined}
          />
        ),
        insert: `@${m.displayName} `,
      }));
    for (const role of roles) {
      if (!role.mentionable || !role.name.toLowerCase().includes(q)) continue;
      people.push({
        key: `role-${role.id}`,
        label: role.name,
        hint:
          role.memberCount === 1
            ? t('Rol · 1 persona')
            : t('Rol · {{count}} personas', { count: role.memberCount }),
        icon: <span className={cn('mx-1 size-2.5 rounded-full', roleColors[role.color].dot)} />,
        insert: `@${role.name} `,
      });
    }
    if (channel.kind === 'channel' && 'canal'.startsWith(q)) {
      people.push({
        key: 'canal',
        label: 'canal',
        hint: t('Avisa a todos en esta conversación'),
        icon: <Users className="size-4" />,
        insert: '@canal ',
      });
    }
    return people;
  })();
  const open = suggestions.length > 0;
  const selected = Math.min(activeIndex, suggestions.length - 1);

  function applySuggestion(suggestion: Suggestion) {
    if (!token) return;
    const next = body.slice(0, token.start) + suggestion.insert + body.slice(caret);
    setActiveIndex(0);
    setBodyWithSelection(next, token.start + suggestion.insert.length);
  }

  function insertAtCaret(text: string) {
    const el = textarea.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    setBodyWithSelection(body.slice(0, start) + text + body.slice(end), start + text.length);
  }

  /** Wraps the selection; with nothing selected the caret lands between the markers. */
  function format(marker: string, closing = marker) {
    const el = textarea.current;
    if (!el) return;
    const result = wrapSelection(body, el.selectionStart, el.selectionEnd, marker, closing, '');
    setBodyWithSelection(result.text, result.selectionStart, result.selectionEnd);
  }

  /* ---------- Files ---------- */

  const patchFile = (localId: string, patch: Partial<PendingFile>) =>
    setFiles((list) => list.map((f) => (f.localId === localId ? { ...f, ...patch } : f)));

  function addFiles(incoming: File[]) {
    if (!canUpload || incoming.length === 0) return;
    const room = MESSAGE_MAX_ATTACHMENTS - files.length;
    if (room <= 0) {
      toast({
        title: t('Máximo {{count}} archivos por mensaje', { count: MESSAGE_MAX_ATTACHMENTS }),
        tone: 'error',
      });
      return;
    }
    for (const file of incoming.slice(0, room)) {
      if (file.size > maxBytes) {
        toast({
          title: t('«{{name}}» supera {{size}}', { name: file.name, size: formatBytes(maxBytes) }),
          tone: 'error',
        });
        continue;
      }
      const pending: PendingFile = {
        localId: crypto.randomUUID(),
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        progress: 0,
        attachment: null,
        error: null,
      };
      setFiles((list) => [...list, pending]);
      api.attachments
        .upload(channel.id, file, {
          onProgress: (progress) => patchFile(pending.localId, { progress }),
        })
        .then(
          (attachment) => patchFile(pending.localId, { attachment, progress: 1 }),
          (err: unknown) => patchFile(pending.localId, { error: errorMessage(err) }),
        );
    }
    textarea.current?.focus();
  }

  useImperativeHandle(ref, () => ({ addFiles }));

  // «Responder» on a message focuses this composer.
  useEffect(() => {
    if (parentId) return;
    const onFocus = (event: Event) => {
      if ((event as CustomEvent<string>).detail === channel.id) textarea.current?.focus();
    };
    window.addEventListener('nexo:focus-composer', onFocus);
    return () => window.removeEventListener('nexo:focus-composer', onFocus);
  }, [channel.id, parentId]);

  function removeFile(localId: string) {
    setFiles((list) => {
      const file = list.find((f) => f.localId === localId);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return list.filter((f) => f.localId !== localId);
    });
  }

  /* ---------- Sending ---------- */

  const send = useMutation({
    mutationFn: ({
      text,
      attachments,
      clientId,
      replyTo,
    }: {
      text: string;
      attachments: Attachment[];
      clientId: string;
      replyTo: Message | null;
    }) =>
      api.messages.send(channel.id, {
        body: text,
        attachmentIds: attachments.map((a) => a.id),
        clientId,
        ...(parentId ? { parentId } : {}),
        ...(replyTo ? { replyToId: replyTo.id } : {}),
      }),
    onMutate: ({ text, attachments, clientId, replyTo }) => {
      const optimistic: Message = {
        id: `temp-${clientId}`,
        clientId,
        channelId: channel.id,
        teamId: channel.teamId,
        author: {
          id: me.id,
          displayName: me.displayName,
          avatarUrl: me.avatarUrl,
          title: me.title,
          status: me.status,
          dnd: me.dnd,
          away: me.away,
        },
        webhookId: null,
        body: text,
        createdAt: new Date().toISOString(),
        editedAt: null,
        tasks: [],
        attachments,
        embeds: [],
        call: null,
        parentId: parentId ?? null,
        replyTo: replyTo
          ? {
              id: replyTo.id,
              author: replyTo.author,
              body: replyTo.body.slice(0, 280),
              attachmentCount: replyTo.attachments.length,
            }
          : null,
        thread: null,
        reactions: [],
        previews: [],
        pinnedAt: null,
        pinnedBy: null,
        meetingId: null,
        poll: null,
      };
      cache.upsertMessage(queryClient, optimistic);
    },
    onSuccess: (message) => cache.upsertMessage(queryClient, message),
    onError: (err, { text, clientId }) => {
      cache.removeMessage(
        queryClient,
        channel.id,
        (m) => m.clientId === clientId && m.id.startsWith('temp-'),
      );
      setBody((current) => current || text);
      toast({
        title: t('No se pudo enviar el mensaje'),
        description: errorMessage(err),
        tone: 'error',
      });
    },
  });

  const uploading = files.some((f) => !f.attachment && !f.error);
  const ready = files.flatMap((f) => (f.attachment ? [f.attachment] : []));
  const canSend = !uploading && (body.trim().length > 0 || ready.length > 0);

  function reset() {
    files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    setBody('');
    localStorage.removeItem(draftKey(draftId));
    lastTyping.current = 0;
  }

  function submit(event?: SubmitEvent) {
    event?.preventDefault();
    if (!canSend) return;
    const text = fromEditable(body.trim(), members, roles);

    // Slash command: /tarea <título> [@responsables]
    const command = /^\/tarea\s+([\s\S]+)$/i.exec(text);
    if (command) {
      const raw = command[1] ?? '';
      const title = raw
        .replace(/<@&?[0-9a-f-]{36}>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!title) return;
      createTask.mutate(
        { title: title.slice(0, 200), channelId: channel.id, assigneeIds: mentionedUserIds(raw) },
        {
          onSuccess: (task) =>
            toast({ title: t('Tarea creada'), description: task.title, tone: 'success' }),
        },
      );
      reset();
      return;
    }

    send.mutate({ text, attachments: ready, clientId: crypto.randomUUID(), replyTo: replyingTo });
    if (replyingTo) replyStore.clear(channel.id);
    // Show your own message even if you were reading older ones.
    if (!parentId)
      window.dispatchEvent(new CustomEvent('nexo:scroll-bottom', { detail: channel.id }));
    reset();
  }

  /** ↑ in an empty composer edits your last message, like in most chat apps. */
  function editLastMessage(): boolean {
    if (parentId) return false;
    const data = queryClient.getQueryData<InfiniteData<Paginated<Message>>>(
      keys.messages(channel.id),
    );
    const newest = data?.pages[0]?.items ?? [];
    const last = [...newest]
      .reverse()
      .find((m) => m.author.id === me.id && !m.webhookId && !m.call && !m.id.startsWith('temp-'));
    if (!last) return false;
    editingStore.set(last.id);
    return true;
  }

  function onChange(value: string, position: number) {
    setBody(value);
    setCaret(position);
    setDismissedAt(null);
    const now = Date.now();
    if (value && !value.startsWith('/') && now - lastTyping.current > TYPING_PING_MS) {
      lastTyping.current = now;
      void api.channels.typing(channel.id, client?.connection.socket_id).catch(() => undefined);
    }
  }

  const placeholder = parentId
    ? t('Responder en el hilo')
    : t('Mensaje para {{name}}', {
        name: channel.kind === 'dm' ? channelLabel(channel) : `#${channel.name}`,
      });
  const isCommand = /^\/tarea\s/i.test(body);

  if (!canIn(channel, 'send_messages')) {
    return (
      <div className="mx-4 mb-4 rounded-2xl border border-border bg-surface-2 px-4 py-3 text-sm text-muted">
        {t('No tienes permiso para enviar mensajes aquí.')}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="relative shrink-0 px-4 pb-4 safe-bottom sm:px-5">
      <ScheduledMessages channel={channel} {...(parentId ? { parentId } : {})} />
      {open && (
        <div
          role="listbox"
          aria-label={token?.kind === '/' ? t('Comandos') : t('Mencionar')}
          className="absolute inset-x-4 bottom-full z-20 mb-1 overflow-hidden rounded-xl border border-border bg-surface p-1 shadow-float sm:inset-x-5"
        >
          <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
            {token?.kind === '/' ? t('Comandos') : t('Mencionar')}
          </p>
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.key}
              type="button"
              role="option"
              aria-selected={index === selected}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => applySuggestion(suggestion)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm',
                index === selected && 'bg-surface-2',
              )}
            >
              <span className="flex w-5 justify-center text-muted">{suggestion.icon}</span>
              <span className="font-medium">{suggestion.label}</span>
              {suggestion.hint && (
                <span className="truncate text-xs text-subtle">{suggestion.hint}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div
        className={cn(
          'rounded-2xl border bg-surface shadow-panel transition-[border-color,box-shadow] focus-within:ring-4',
          isCommand
            ? 'border-accent ring-accent/10'
            : 'border-border-strong focus-within:border-accent focus-within:ring-accent/10',
        )}
      >
        {replyingTo && (
          <QuoteBlock
            className="m-2 mb-0 max-w-none!"
            quote={{
              id: replyingTo.id,
              author: replyingTo.author,
              body: replyingTo.body,
              attachmentCount: replyingTo.attachments.length,
            }}
            onClose={() => replyStore.clear(channel.id)}
          />
        )}
        {showFormatting && (
          <div
            className="flex items-center gap-0.5 border-b border-border px-2 py-1"
            role="toolbar"
            aria-label={t('Formato')}
          >
            <FormatButton label={t('Negrita (Ctrl+B)')} onClick={() => format('**')}>
              <Bold />
            </FormatButton>
            <FormatButton label={t('Cursiva (Ctrl+I)')} onClick={() => format('*')}>
              <Italic />
            </FormatButton>
            <FormatButton label={t('Tachado (Ctrl+Shift+X)')} onClick={() => format('~~')}>
              <Strikethrough />
            </FormatButton>
            <span className="mx-1 h-4 w-px bg-border" />
            <FormatButton label={t('Código (Ctrl+E)')} onClick={() => format('`')}>
              <Code />
            </FormatButton>
            <FormatButton label={t('Bloque de código')} onClick={() => format('```\n', '\n```')}>
              <SquareCode />
            </FormatButton>
          </div>
        )}

        {files.length > 0 && (
          <ul className="flex flex-wrap gap-2 px-3 pt-3" aria-label={t('Archivos adjuntos')}>
            {files.map((f) => (
              <li
                key={f.localId}
                className={cn(
                  'relative flex h-16 max-w-56 items-center gap-2.5 overflow-hidden rounded-xl border bg-surface-2 pr-8',
                  f.error ? 'border-danger/50' : 'border-border',
                  f.previewUrl ? 'w-16 pr-0' : 'pl-3',
                )}
              >
                {f.previewUrl ? (
                  <img src={f.previewUrl} alt={f.file.name} className="size-full object-cover" />
                ) : (
                  <>
                    <FileIcon
                      contentType={f.file.type}
                      name={f.file.name}
                      className="size-5 shrink-0 text-accent"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium">{f.file.name}</span>
                      <span
                        className={cn('block text-[11px]', f.error ? 'text-danger' : 'text-subtle')}
                      >
                        {f.error ?? formatBytes(f.file.size)}
                      </span>
                    </span>
                  </>
                )}
                {!f.attachment && !f.error && (
                  <span className="absolute inset-x-0 bottom-0 h-1 bg-surface-3">
                    <span
                      className="block h-full bg-accent transition-[width]"
                      style={{ width: `${Math.round(f.progress * 100)}%` }}
                    />
                  </span>
                )}
                <button
                  type="button"
                  aria-label={t('Quitar {{name}}', { name: f.file.name })}
                  onClick={() => removeFile(f.localId)}
                  className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-ink/70 text-ink-fg hover:bg-ink"
                >
                  <X className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <textarea
          ref={textarea}
          rows={1}
          value={body}
          maxLength={MESSAGE_MAX_LENGTH}
          aria-label={t('Mensaje')}
          aria-autocomplete="list"
          aria-expanded={open}
          placeholder={t('{{placeholder}} · @ mencionar · / comandos', { placeholder })}
          onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onPaste={(e) => {
            const pasted = Array.from(e.clipboardData.files);
            if (pasted.length > 0 && canUpload) {
              e.preventDefault();
              addFiles(pasted);
            }
          }}
          onKeyDown={(e) => {
            const mod = e.ctrlKey || e.metaKey;
            if (open) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const delta = e.key === 'ArrowDown' ? 1 : -1;
                setActiveIndex((selected + delta + suggestions.length) % suggestions.length);
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const suggestion = suggestions[selected];
                if (suggestion) applySuggestion(suggestion);
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setDismissedAt(caret);
                return;
              }
            }
            if (mod && e.key.toLowerCase() === 'b') {
              e.preventDefault();
              format('**');
            } else if (mod && e.key.toLowerCase() === 'i') {
              e.preventDefault();
              format('*');
            } else if (mod && e.key.toLowerCase() === 'e') {
              e.preventDefault();
              format('`');
            } else if (mod && e.shiftKey && e.key.toLowerCase() === 'x') {
              e.preventDefault();
              format('~~');
            } else if (e.key === 'Escape' && replyingTo) {
              e.preventDefault();
              replyStore.clear(channel.id);
            } else if (e.key === 'ArrowUp' && !body && files.length === 0) {
              if (editLastMessage()) e.preventDefault();
            } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          className="block max-h-[240px] min-h-11 w-full resize-none bg-transparent px-4 pt-3 pb-1 text-[14.5px] placeholder:text-subtle focus:outline-none"
        />

        <div className="flex items-center px-2.5 pt-1 pb-2.5">
          <div
            className="flex items-center gap-1"
            role="toolbar"
            aria-label={t('Herramientas del mensaje')}
          >
            {canUpload && (
              <>
                <IconButton
                  label={t('Adjuntar archivos')}
                  onClick={() => fileInput.current?.click()}
                >
                  <Paperclip />
                </IconButton>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files ?? []));
                    e.target.value = '';
                  }}
                />
                <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              </>
            )}
            <IconButton
              label={t('Formato')}
              active={showFormatting}
              onClick={() => setShowFormatting((v) => !v)}
            >
              <Type />
            </IconButton>
            <EmojiPicker onPick={insertAtCaret} />
            <IconButton
              label={t('Mencionar a alguien')}
              onClick={() => insertAtCaret(body && !/\s$/.test(body) ? ' @' : '@')}
            >
              <AtSign />
            </IconButton>
            <IconButton
              label={t('Crear tarea (/tarea)')}
              onClick={() => !body.startsWith('/tarea') && setBody(`/tarea ${body}`)}
            >
              <ListPlus />
            </IconButton>
            {canIn(channel, 'send_messages') && (
              <IconButton label={t('Crear encuesta')} onClick={() => setCreatingPoll(true)}>
                <BarChart3 />
              </IconButton>
            )}
            {canIn(channel, 'send_messages') && (
              <IconButton
                label={t('Programar envío')}
                disabled={!body.trim() || body.startsWith('/') || files.length > 0}
                onClick={() => setScheduling(true)}
              >
                <CalendarClock />
              </IconButton>
            )}
            {scheduling && (
              <WhenDialog
                title={t('Programar envío')}
                description={t('Se enviará en tu nombre en el momento que elijas.')}
                confirmLabel={t('Programar')}
                loading={schedule.isPending}
                onPick={(at) => schedule.mutate(at)}
                onClose={() => setScheduling(false)}
              />
            )}
            {creatingPoll && (
              <CreatePollDialog
                channel={channel}
                {...(parentId ? { parentId } : {})}
                open
                onClose={() => setCreatingPoll(false)}
              />
            )}
          </div>

          <span className="flex-1" />
          {isCommand ? (
            <span className="mr-3 truncate text-xs font-medium text-accent">
              {t('Enter creará una tarea en esta conversación')}
            </span>
          ) : parentId ? null : (
            <span className="mr-3 hidden items-center gap-4 text-[11px] text-subtle lg:flex">
              <Hint keys={['Enter']} label={t('enviar')} />
              <Hint keys={['Shift', 'Enter']} label={t('nueva línea')} />
              <Hint keys={['↑']} label={t('editar el último')} />
            </span>
          )}
          {body.length >= COUNTER_FROM && (
            <span
              className={cn(
                'mr-2 text-[11px] tabular-nums',
                body.length >= MESSAGE_MAX_LENGTH ? 'text-danger' : 'text-warning',
              )}
            >
              {MESSAGE_MAX_LENGTH - body.length}
            </span>
          )}
          <button
            type="submit"
            aria-label={isCommand ? t('Crear tarea') : t('Enviar')}
            disabled={!canSend}
            className="inline-flex size-8 items-center justify-center rounded-lg bg-accent text-accent-fg shadow-panel transition-[background-color,opacity] hover:bg-accent-hover disabled:bg-surface-3 disabled:text-subtle disabled:shadow-none"
          >
            {isCommand ? <ListPlus className="size-4" /> : <SendHorizontal className="size-4" />}
          </button>
        </div>
      </div>
    </form>
  );
});

function FormatButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <IconButton label={label} size="sm" onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {children}
    </IconButton>
  );
}

function Hint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="inline-flex items-center gap-0.5">
        {keys.map((key, i) => (
          <span key={key} className="inline-flex items-center gap-0.5">
            {i > 0 && <span className="text-subtle">+</span>}
            <Kbd>{key}</Kbd>
          </span>
        ))}
      </span>
      {label}
    </span>
  );
}
